<div class="fixed bottom-0 top-0 w-full" *transloco="let t">
    <div
        class="bg-card flex h-full w-full flex-col transition-transform duration-400 ease-drawer md:w-2/5"
        [ngClass]="{ '-translate-x-full shadow': opened, 'translate-x-0': !opened }"
    >
        <!-- Header -->
        <div class="ai-chat-header flex flex-0 items-center justify-start">
            <div class="flex flex-auto items-center justify-between">
                <div class="flex flex-auto items-center">
                    <div class="flex w-16 flex-0 items-center justify-center">
                        <mat-icon class="icon-size-6" [svgIcon]="'treebyte-ai-chat'"></mat-icon>
                    </div>
                    <div class="text-secondary text-lg font-medium">
                        {{ t('components.ai-chat.ai-chat-title', { botName: AI_CONSTANTS.BOT_NAME }) }}
                    </div>
                </div>
                <div class="flex items-center">
                    @if (models().length > 1) {
                        <!-- Model of the conversation: chosen before the first message, then fixed -->
                        <mat-button-toggle-group
                            class="ai-chat-model mr-2"
                            hideSingleSelectionIndicator=""
                            [value]="modelKey()"
                            [disabled]="conversationId !== null || busy()"
                            (change)="selectModel($event.value)"
                            [matTooltip]="t('components.ai-chat.model-tooltip')"
                        >
                            @for (model of models(); track model) {
                                <mat-button-toggle [value]="model">{{ modelLabel(model) }}</mat-button-toggle>
                            }
                        </mat-button-toggle-group>
                    }
                    <button mat-icon-button (click)="openHistory()" [matTooltip]="t('components.ai-chat.history')">
                        <mat-icon [svgIcon]="'heroicons_outline:clock'"></mat-icon>
                    </button>
                    <button mat-icon-button (click)="initChat()" [matTooltip]="t('components.ai-chat.new-chat')">
                        <mat-icon [svgIcon]="'heroicons_outline:plus'"></mat-icon>
                    </button>
                    <button class="mr-4" mat-icon-button (click)="toggle()" [matTooltip]="t('generic.close')">
                        <mat-icon [svgIcon]="'heroicons_outline:x-mark'"></mat-icon>
                    </button>
                </div>
            </div>
        </div>

        <!-- Content -->
        <div class="flex flex-auto overflow-hidden border-t">
            <div
                class="flex flex-auto flex-col justify-between overflow-hidden border-l bg-gray-50 dark:bg-transparent"
            >
                @if (showHistory()) {
                    <!-- Past conversations -->
                    <div class="flex flex-auto flex-col overflow-y-auto p-4">
                        @for (conversation of conversations(); track conversation.id) {
                            <div
                                class="flex cursor-pointer items-center rounded-lg px-3 py-2 hover:bg-hover"
                                (click)="loadConversation(conversation.id)"
                            >
                                <div class="min-w-0 flex-auto">
                                    <div class="truncate font-medium">{{ conversation.title }}</div>
                                    <div class="text-secondary text-sm">
                                        {{ conversation.lastActivityTime | date: 'dd/MM/yyyy HH:mm' }}
                                    </div>
                                </div>
                                <button
                                    mat-icon-button
                                    (click)="deleteConversation(conversation.id, $event)"
                                    [matTooltip]="t('generic.delete')"
                                >
                                    <mat-icon [svgIcon]="'heroicons_outline:trash'"></mat-icon>
                                </button>
                            </div>
                        } @empty {
                            <div class="text-secondary p-4 text-center">
                                {{ t('components.ai-chat.no-conversations') }}
                            </div>
                        }
                    </div>
                } @else {
                    <!-- Conversation -->
                    <div class="flex flex-col-reverse overflow-y-auto overscroll-y-contain">
                        <div class="flex flex-auto shrink flex-col gap-3 p-6">
                            @for (message of messages(); track $index) {
                                <div
                                    class="flex flex-col"
                                    [ngClass]="{
                                        'items-end': message.role === 'user',
                                        'items-start': message.role === 'assistant',
                                    }"
                                >
                                    @if (message.tools.length) {
                                        <div class="mb-1 flex flex-wrap gap-1">
                                            @for (tool of message.tools; track tool) {
                                                <span
                                                    class="text-secondary rounded-full bg-gray-200 px-2 py-0.5 text-xs dark:bg-gray-700"
                                                >
                                                    {{ toolLabel(tool) }}
                                                </span>
                                            }
                                        </div>
                                    }
                                    @if (message.role === 'user') {
                                        <div
                                            class="max-w-3/4 whitespace-pre-wrap rounded-lg bg-blue-500 px-3 py-2 text-blue-50"
                                        >
                                            {{ message.text }}
                                        </div>
                                    } @else {
                                        <div
                                            class="ai-chat-markdown max-w-full rounded-lg px-3 py-2"
                                            [ngClass]="{
                                                'bg-card border': !message.error,
                                                'border border-warn-300 bg-warn-50': message.error,
                                            }"
                                        >
                                            @if (message.streaming && !message.text) {
                                                <span class="text-secondary italic">
                                                    {{ t('components.ai-chat.thinking') }}
                                                </span>
                                            }
                                            <div [innerHTML]="message.html"></div>
                                            <!-- Proposed writes: preview computed by the server, decided by the user -->
                                            @for (proposal of message.proposals ?? []; track proposal.requestId) {
                                                <div class="mt-3 rounded-lg border p-3">
                                                    <div class="mb-2 font-semibold">{{ proposal.title }}</div>
                                                    @if (proposal.rows.length) {
                                                        <div class="ai-chat-markdown overflow-x-auto">
                                                            <table>
                                                                <thead>
                                                                    <tr>
                                                                        @for (
                                                                            column of proposal.columns;
                                                                            track $index
                                                                        ) {
                                                                            <th>{{ column }}</th>
                                                                        }
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    @for (row of proposal.rows; track $index) {
                                                                        <tr>
                                                                            @for (cell of row; track $index) {
                                                                                <td>{{ cell }}</td>
                                                                            }
                                                                        </tr>
                                                                    }
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    }
                                                    @for (error of proposal.errors; track $index) {
                                                        <div class="mt-1 text-sm text-warn">{{ error }}</div>
                                                    }
                                                    @if (proposal.state === 'pending') {
                                                        <div class="mt-3 flex gap-2">
                                                            <button
                                                                mat-flat-button
                                                                color="primary"
                                                                [disabled]="busy() || !proposal.canApprove"
                                                                (click)="decide(proposal, true)"
                                                            >
                                                                {{ t('components.ai-chat.approve') }}
                                                            </button>
                                                            <button
                                                                mat-stroked-button
                                                                [disabled]="busy()"
                                                                (click)="decide(proposal, false)"
                                                            >
                                                                {{ t('components.ai-chat.reject') }}
                                                            </button>
                                                        </div>
                                                    } @else {
                                                        <div class="text-secondary mt-2 text-sm italic">
                                                            {{
                                                                proposal.state === 'approved'
                                                                    ? t('components.ai-chat.approved')
                                                                    : t('components.ai-chat.rejected')
                                                            }}
                                                            @if (awaitsOthers(message, proposal)) {
                                                                — {{ t('components.ai-chat.decide-others') }}
                                                            }
                                                        </div>
                                                    }
                                                </div>
                                            }
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    </div>
                }

                <!-- Message field -->
                <div class="sticky bottom-0 flex items-end border-t bg-gray-50 p-4 dark:bg-transparent">
                    <mat-form-field
                        class="fuse-mat-dense fuse-mat-rounded fuse-mat-bold w-full"
                        [subscriptSizing]="'dynamic'"
                        (keydown.enter)="chatWithAi($event, messageInput)"
                    >
                        <textarea
                            matInput
                            cdkTextareaAutosize
                            cdkAutosizeMaxRows="6"
                            class="max-h-2/5"
                            [placeholder]="t('components.ai-chat.placeholder')"
                            #messageInput
                        ></textarea>
                    </mat-form-field>
                    <div class="my-px ml-4 flex h-11 items-center">
                        <button
                            [disabled]="busy()"
                            mat-icon-button
                            (click)="chatWithAi($event, messageInput)"
                            [matTooltip]="t('generic.send')"
                        >
                            <mat-icon [svgIcon]="'heroicons_outline:paper-airplane'"></mat-icon>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
